import { useEffect, useId, useRef, useState } from 'react'
import useEscapeKey from '../../hooks/useEscapeKey'
import { formatTime } from './clipUtils'

type Source = 'webcam' | 'screen' | 'pip'

const SOURCES: { id: Source; label: string; needs: string }[] = [
  { id: 'webcam', label: 'Webcam', needs: 'your camera and microphone' },
  { id: 'screen', label: 'Screen', needs: 'your screen and microphone' },
  { id: 'pip', label: 'Screen + webcam', needs: 'your screen, camera and microphone' },
]

// Recordings stop on their own after this long.
export const MAX_RECORD_SECONDS = 30 * 60
// The webcam's share of the width in screen + webcam recordings.
const PIP_SHARE = 0.25

interface RecordDialogProps {
  isOpen: boolean
  onClose: () => void
  /** The finished recording, as a file for the usual resumable upload. */
  onRecorded: (file: File, seconds: number) => void
}

/** Why a capture call failed, in words the user can act on. */
export const permissionMessage = (error: unknown): string => {
  const name = (error as { name?: string })?.name
  if (name === 'NotAllowedError' || name === 'SecurityError')
    return 'Permission denied. Allow camera, microphone and screen access for this site in your browser settings, then try again.'
  if (name === 'NotFoundError' || name === 'OverconstrainedError')
    return 'No camera or microphone was found. Plug one in, or record your screen instead.'
  if (name === 'NotReadableError') return 'Your camera or microphone is in use by another app. Close it and try again.'
  return "Recording couldn't start in this browser."
}

const pickMimeType = (): string | undefined =>
  ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'].find((t) =>
    MediaRecorder.isTypeSupported?.(t)
  )

const stopAll = (streams: MediaStream[]) => streams.forEach((s) => s.getTracks().forEach((t) => t.stop()))

/** Screen with the webcam in the bottom-right corner, drawn onto a canvas. */
const composite = (screen: MediaStream, camera: MediaStream): { stream: MediaStream; stop: () => void } => {
  const screenVideo = document.createElement('video')
  const cameraVideo = document.createElement('video')
  for (const [el, src] of [[screenVideo, screen], [cameraVideo, camera]] as const) {
    el.srcObject = src
    el.muted = true
    el.play?.()?.catch?.(() => undefined)
  }
  const settings = screen.getVideoTracks()[0]?.getSettings?.() || {}
  const canvas = document.createElement('canvas')
  canvas.width = settings.width || 1280
  canvas.height = settings.height || 720
  const ctx = canvas.getContext('2d')
  let raf = 0
  const draw = () => {
    if (ctx) {
      ctx.drawImage(screenVideo, 0, 0, canvas.width, canvas.height)
      const w = canvas.width * PIP_SHARE
      const h = (w * (cameraVideo.videoHeight || 3)) / (cameraVideo.videoWidth || 4)
      const margin = canvas.width * 0.02
      ctx.drawImage(cameraVideo, canvas.width - w - margin, canvas.height - h - margin, w, h)
    }
    raf = requestAnimationFrame(draw)
  }
  draw()
  const stream = canvas.captureStream(30)
  return { stream, stop: () => cancelAnimationFrame(raf) }
}

/**
 * Record in the browser: webcam, screen, or screen with the webcam in a
 * corner, with MediaRecorder, for up to 30 minutes. Explains what it will ask
 * for before the browser asks.
 */
const RecordDialog = ({ isOpen, onClose, onRecorded }: RecordDialogProps) => {
  const titleId = useId()
  const [source, setSource] = useState<Source>('webcam')
  const [phase, setPhase] = useState<'choose' | 'recording'>('choose')
  const [error, setError] = useState<string | null>(null)
  const [seconds, setSeconds] = useState(0)
  const preview = useRef<HTMLVideoElement>(null)
  const recorder = useRef<MediaRecorder | null>(null)
  const cleanup = useRef<() => void>(() => undefined)
  const timer = useRef<ReturnType<typeof setInterval>>()

  const stop = () => {
    if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop()
  }

  const reset = () => {
    clearInterval(timer.current)
    cleanup.current()
    cleanup.current = () => undefined
    recorder.current = null
    setPhase('choose')
    setSeconds(0)
  }

  const close = () => {
    if (recorder.current && recorder.current.state !== 'inactive') {
      recorder.current.ondataavailable = null
      recorder.current.onstop = null
      recorder.current.stop()
    }
    reset()
    setError(null)
    onClose()
  }

  useEscapeKey(close, isOpen && phase === 'choose')
  useEffect(() => () => cleanup.current(), [])

  const start = async () => {
    setError(null)
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices) {
      setError("This browser can't record video. Try a recent Chrome, Edge or Firefox.")
      return
    }
    const streams: MediaStream[] = []
    let stopComposite = () => undefined as void
    try {
      let stream: MediaStream
      if (source === 'webcam') {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
        streams.push(stream)
      } else {
        const screen = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false })
        streams.push(screen)
        const mic = await navigator.mediaDevices.getUserMedia({ video: source === 'pip', audio: true })
        streams.push(mic)
        let video = screen
        if (source === 'pip') {
          const pip = composite(screen, mic)
          stopComposite = pip.stop
          video = pip.stream
        }
        stream = new MediaStream([...video.getVideoTracks(), ...mic.getAudioTracks()])
      }
      cleanup.current = () => {
        stopComposite()
        stopAll(streams)
      }
      if (preview.current) {
        preview.current.srcObject = stream
        preview.current.play?.()?.catch?.(() => undefined)
      }
      const mimeType = pickMimeType()
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      const chunks: Blob[] = []
      const began = Date.now()
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data)
      rec.onstop = () => {
        const length = Math.round((Date.now() - began) / 1000)
        const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-')
        const file = new File(chunks, `recording-${stamp}.webm`, { type: 'video/webm' })
        reset()
        onRecorded(file, length)
      }
      // Ending a screen share from the browser's own bar stops the recording too.
      streams[0].getVideoTracks()[0]?.addEventListener?.('ended', () => stop())
      recorder.current = rec
      rec.start(1000)
      setPhase('recording')
      timer.current = setInterval(() => {
        const elapsed = Math.round((Date.now() - began) / 1000)
        setSeconds(elapsed)
        if (elapsed >= MAX_RECORD_SECONDS) stop()
      }, 1000)
    } catch (e) {
      stopComposite()
      stopAll(streams)
      setError(permissionMessage(e))
    }
  }

  if (!isOpen) return null
  const needs = SOURCES.find((s) => s.id === source)?.needs

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="w-full max-w-md rounded-lg bg-white p-6">
        <h2 id={titleId} className="text-lg font-bold text-gray-900">
          Record a video
        </h2>
        {phase === 'choose' ? (
          <>
            <div role="radiogroup" aria-label="What to record" className="mt-3 flex flex-wrap gap-2">
              {SOURCES.map((s) => (
                <label
                  key={s.id}
                  className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm ${
                    source === s.id ? 'border-primary-600 bg-primary-50 text-primary-600' : 'border-gray-300 text-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    name="record-source"
                    className="h-4 w-4"
                    checked={source === s.id}
                    onChange={() => setSource(s.id)}
                  />
                  {s.label}
                </label>
              ))}
            </div>
            <p className="mt-3 text-sm text-gray-600" data-testid="permission-explanation">
              Your browser will ask to use {needs}. Clips only uses them while you record, and nothing leaves your
              computer until you stop; then the recording uploads like any video. Recordings stop after 30 minutes.
            </p>
          </>
        ) : (
          <div className="mt-3">
            <p className="mt-2 text-sm text-gray-700" aria-live="polite">
              Recording · {formatTime(seconds)} of {formatTime(MAX_RECORD_SECONDS)}
            </p>
          </div>
        )}
        {/* One element throughout, so the stream set on it before the phase changes stays. */}
        <video
          ref={preview}
          muted
          playsInline
          className={phase === 'recording' ? 'mt-3 aspect-video w-full rounded-md bg-black' : 'hidden'}
        />
        {error && (
          <p role="alert" className="mt-3 text-sm text-danger-600">
            {error}
          </p>
        )}
        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={close}
            className="flex-1 rounded-md border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          {phase === 'choose' ? (
            <button
              type="button"
              onClick={start}
              className="flex-1 rounded-md bg-primary-600 px-4 py-2 font-medium text-white hover:bg-primary-700"
            >
              Start recording
            </button>
          ) : (
            <button
              type="button"
              onClick={stop}
              className="flex-1 rounded-md bg-danger-500 px-4 py-2 font-medium text-white hover:bg-danger-700"
            >
              Stop and upload
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default RecordDialog
